import * as Yup from "yup";

// Section 1: Personal Details
export const PersonalDetailsValidationSchema = Yup.object().shape({
  fullName: Yup.string()
    .required("Full name is required")
    .min(2, "Full name must be at least 2 characters")
    .max(100, "Full name must not exceed 100 characters"),

  callingName: Yup.string()
    .required("Calling name is required")
    .min(2, "Calling name must be at least 2 characters")
    .max(50, "Calling name must not exceed 50 characters"),

  nicNumber: Yup.string()
    .required("NIC number is required")
    .min(5, "NIC number must be at least 5 characters")
    .max(20, "NIC number must not exceed 20 characters")
    .matches(
      /^[a-zA-Z0-9]+$/,
      "NIC number can only contain letters and numbers"
    ),

  dateOfBirth: Yup.date()
    .required("Date of birth is required")
    .max(new Date(), "Date of birth cannot be in the future")
    .test("age", "You must be at least 16 years old", function (value) {
      if (!value) return false;
      const cutoff = new Date();
      cutoff.setFullYear(cutoff.getFullYear() - 16);
      return value <= cutoff;
    }),

  portrait: Yup.mixed()
    .required("Portrait photo is required")
    .test(
      "fileSize",
      "Portrait file size must be less than 5MB",
      (value) => !value || (value && value.size <= 5 * 1024 * 1024)
    )
    .test(
      "fileType",
      "Portrait must be an image (JPEG, PNG, JPG)",
      (value) =>
        !value ||
        (value && ["image/jpeg", "image/png", "image/jpg"].includes(value.type))
    ),
});
// Section 2: Contact Details
export const ContactDetailsValidationSchema = Yup.object().shape({
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email address"),

  mobileCountryCode: Yup.string().required("Mobile country code is required"),

  mobileNumber: Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]{7,15}$/, "Invalid mobile number format"),

  fixedLineCountryCode: Yup.string().test(
    "both-or-neither",
    "Both fixed line country code and number are required",
    function (value) {
      const { fixedLineNumber } = this.parent;
      if (value && !fixedLineNumber) return false;
      if (!value && fixedLineNumber) return false;
      return true;
    }
  ),

  fixedLineNumber: Yup.string()
    .test("valid-format", "Invalid fixed line number format", function (value) {
      if (!value) return true;
      return /^[0-9]{7,15}$/.test(value);
    })
    .test(
      "both-or-neither",
      "Both fixed line country code and number are required",
      function (value) {
        const { fixedLineCountryCode } = this.parent;
        if (value && !fixedLineCountryCode) return false;
        if (!value && fixedLineCountryCode) return false;
        return true;
      }
    ),

  whatsappCountryCode: Yup.string().required(
    "WhatsApp country code is required"
  ),

  whatsappNumber: Yup.string()
    .required("WhatsApp number is required")
    .matches(/^[0-9]{7,15}$/, "Invalid WhatsApp number format"),

  address: Yup.string()
    .required("Address is required")
    .min(10, "Address must be at least 10 characters")
    .max(200, "Address must not exceed 200 characters"),

  province: Yup.number().required("Province is required"),

  district: Yup.number().required("District is required"),

  city: Yup.number().required("City is required"),

  abroad: Yup.boolean(),

  currentCountry: Yup.string().when("abroad", {
    is: true,
    then: (schema) => schema.required("Current country is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

// Section 3: School Timeline
export const SchoolTimelineValidationSchema = Yup.object().shape({
  schoolEnrollmentYear: Yup.number()
    .required("School enrollment year is required")
    .min(1950, "Year must be after 1950")
    .max(new Date().getFullYear(), "Year cannot be in the future"),

  schoolEnrollmentGrade: Yup.string().required(
    "School enrollment grade is required"
  ),

  lastPassedYear: Yup.number()
    .required("Last passed year is required")
    .min(1950, "Year must be after 1950")
    .max(new Date().getFullYear(), "Year cannot be in the future")
    .test(
      "after-enrollment",
      "Last passed year must be after enrollment year",
      function (value) {
        const { schoolEnrollmentYear } = this.parent;
        if (!schoolEnrollmentYear || !value) return true;
        return value >= schoolEnrollmentYear;
      }
    ),

  lastPassedGrade: Yup.string().required("Last passed grade is required"),

  batch: Yup.string().required("Batch is required"),

  advancedLevelStream: Yup.string().notRequired(),
});

// Section 4: School Engagements
export const SchoolEngagementsValidationSchema = Yup.object().shape({
  // studentEngagements: Yup.array().of(
  //   Yup.object().shape({
  //     startYear: Yup.number()
  //       .notRequired()
  //       .min(1950, "Year must be after 1950")
  //       .max(new Date().getFullYear(), "Year cannot be in the future"),
  //     endYear: Yup.number()
  //       .notRequired()
  //       .min(1950, "Year must be after 1950")
  //       .max(new Date().getFullYear(), "Year cannot be in the future")
  //       .test(
  //         "after-start",
  //         "End year must be after or equal to start year",
  //         function (value) {
  //           const { startYear } = this.parent;
  //           if (!startYear || !value) return true;
  //           return value >= startYear;
  //         }
  //       ),
  //     designation: Yup.string()
  //       .notRequired()
  //       .min(2, "Designation must be at least 2 characters"),
  //   })
  // ),
});

// Section 5: Higher Education
export const HigherEducationValidationSchema = Yup.object().shape({
  // higherEducation: Yup.array().of(
  //   Yup.object().shape({
  //     level: Yup.string().notRequired(),
  //     qualification: Yup.string()
  //       .notRequired()
  //       .min(2, "Qualification must be at least 2 characters"),
  //     institute: Yup.string()
  //       .notRequired()
  //       .min(2, "Institute name must be at least 2 characters"),
  //     year: Yup.number()
  //       .notRequired()
  //       .min(1950, "Year must be after 1950")
  //       .max(new Date().getFullYear() + 5, "Year seems unrealistic"),
  //   })
  // ),
});

// Section 6: Professional Details
export const ProfessionalDetailsValidationSchema = Yup.object().shape({
  industry: Yup.string().notRequired(),

  profession: Yup.string().notRequired(),

  designation: Yup.string().notRequired(),

  designationOther: Yup.string().notRequired(),

  employerName: Yup.string().notRequired(),

  employerAddress: Yup.string().notRequired(),

  officeNumberCountryCode: Yup.string().test(
    "both-or-neither",
    "Both office number country code and number are required",
    function (value) {
      const { officeNumber } = this.parent;
      if (value && !officeNumber) return false;
      if (!value && officeNumber) return false;
      return true;
    }
  ),

  officeNumber: Yup.string()
    .test("valid-format", "Invalid office number format", function (value) {
      if (!value) return true;
      return /^[0-9]{7,15}$/.test(value);
    })
    .test(
      "both-or-neither",
      "Both office number country code and number are required",
      function (value) {
        const { officeNumberCountryCode } = this.parent;
        if (value && !officeNumberCountryCode) return false;
        if (!value && officeNumberCountryCode) return false;
        return true;
      }
    ),
});

// Section 7: Verification Details
export const VerificationDetailsValidationSchema = Yup.object().shape({
  enrollmentNumber: Yup.string().notRequired(),

  proofDocument: Yup.mixed()
    .notRequired()
    .test(
      "enrollment-or-proof",
      "Either a proof document or enrollment number is required",
      function (value) {
        const { enrollmentNumber } = this.parent;
        // Check if at least one is provided
        return !!(value || enrollmentNumber);
      }
    )
    .test(
      "fileSize",
      "Proof document file size must be less than 10MB",
      (value) => {
        if (!value) return true;
        return value.size <= 10 * 1024 * 1024;
      }
    )
    .test(
      "fileType",
      "Proof document must be PDF, JPEG, PNG, or JPG",
      (value) => {
        if (!value) return true;
        return [
          "application/pdf",
          "image/jpeg",
          "image/png",
          "image/jpg",
        ].includes(value.type);
      }
    ),
});

// Section 8: Payment Verification
export const PaymentVerificationValidationSchema = Yup.object().shape({
  paymentSlip: Yup.mixed()
    .required("Payment slip is required")
    .test(
      "fileSize",
      "Payment slip file size must be less than 10MB",
      (value) => !value || (value && value.size <= 10 * 1024 * 1024)
    )
    .test(
      "fileType",
      "Payment slip must be PDF, JPEG, PNG, or JPG",
      (value) =>
        !value ||
        (value &&
          ["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(
            value.type
          ))
    ),
});

// Section 9: Donations
export const DonationsValidationSchema = Yup.object().shape({
  // donation: Yup.object().shape({
  //   donationDate: Yup.date()
  //     .notRequired()
  //     .max(new Date(), "Donation date cannot be in the future"),
  // donationAmount: Yup.number()
  //   .notRequired()
  //   .min(0, "Donation amount must be positive"),
  // donationReason: Yup.string()
  //   .notRequired()
  //   .max(500, "Donation reason must not exceed 500 characters"),
  // }),
  donationSlip: Yup.mixed()
    .notRequired()
    .test(
      "fileSize",
      "Donation slip file size must be less than 10MB",
      (value) => !value || (value && value.size <= 10 * 1024 * 1024)
    )
    .test(
      "fileType",
      "Donation slip must be PDF, JPEG, PNG, or JPG",
      (value) =>
        !value ||
        (value &&
          ["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(
            value.type
          ))
    ),
});

// Section 10: Terms and Conditions
export const TermsAndConditionsValidationSchema = Yup.object().shape({
  termsAccepted: Yup.boolean().oneOf(
    [true],
    "You must accept the terms and conditions"
  ),
});
